import { put, call, select } from 'redux-saga/effects';
import { DefaultUser } from 'constants/defaultValues';
import initPendo from 'helpers/pendoHelper';
import { createUser, getTags } from 'services/api/participant';
import { axiosInstance } from 'services/api/base';
import { changeUserPassword, getAuthenticatedUsers } from 'services/api/user';
import { getOrganizations } from 'services/api/assessment';
import { updateLastLogin } from 'services/api/provider';
import { FeatureFlagsActions } from 'store/feature-flags';
import { useHistory } from 'react-router-dom';
// eslint-disable-next-line import/no-cycle
import Swal from 'sweetalert2';
import {
  setSessionData,
  getSessionDataByKey,
  AUTHENTICATED_USER,
} from 'helpers/sessionStorage';
import {
  UserActions as Actions,
  UserTypes as Types,
  UserSelectors as Selectors,
} from './userReducer';
import {
  OutcomesFeatureFlagsActions,
} from '../outcomes-feature-flags/featureFlagsReducer';

function* setChangePassword({ participantId, password }) {
  yield put(Actions.setChangePasswordLoading(true));

  try {
    yield call(changeUserPassword, participantId, password);

    Swal.fire({
      title: 'Success',
      text: 'Password change correctly',
      icon: 'success',
    });
    yield put(Actions.setChangePasswordOk(true));
  } catch (error) {
    Swal.fire({
      title: 'Error',
      text: 'Failed to change password, try again later or contact an administrator',
      icon: 'error',
    });
    yield put(
      Actions.setUserErrors({
        error,
        message: '[changeUserPassword]: error setting the password',
      }),
    );
  } finally {
    yield put(Actions.setChangePasswordLoading(false));
  }
}

function* setCreateUser({ values }) {
  yield put(Actions.setCreateUserLoading(true));

  try {
    yield call(createUser, values);
    yield put(Actions.setCreateUserOk(true));

    Swal.fire({
      title: 'Saved',
      icon: 'warning',
    });
  } catch (err) {
    const code = err.response.data?.message || err.response.data?.error?.message || '';
    let text;

    if (code === 'Error: User aleady exist in dictionary') {
      text = 'Error External Id for this user already exists in this organization';
    } else if (code === 'User Email already in use') {
      text = 'This email is already in use';
    } else {
      text = "Error couldn't create user, try again later";
    }

    Swal.fire({
      title: 'Error',
      text,
      icon: 'info',
    });

    yield put(
      Actions.setUserErrors({
        error: err,
        message: '[createUser]: Failed to save new user',
      }),
    );
  } finally {
    yield put(Actions.setCreateUserLoading(false));
  }
}

function* fetchOrganizations() {
  try {
    yield put(Actions.setOrganizationsLoading(true));
    const response = yield call(getOrganizations);

    yield put(Actions.fetchOrganizationsSuccess(response));
  } catch (error) {
    yield put(
      Actions.setUserErrors({
        error,
        message: '[createUser]: Failed to save new user',
      }),
    );
    yield put(Actions.fetchOrganizationsSuccess(null));
  } finally {
    yield put(Actions.setOrganizationsLoading(false));
  }
}

export function* setUpdateLastLogin(userId) {
  try {
    yield call(updateLastLogin, userId);
  } catch (error) {
    yield put(
      Actions.setUserErrors({
        error,
        message: '[updateLastLogin]: problem updating last login',
      }),
    );
  }
}

function* setLastLogin(action) {
  const { userId } = action;

  try {
    yield call(setUpdateLastLogin, userId);
  } catch (error) {
    yield put(
      Actions.setUserErrors({
        error,
        message: '[setUpdateLastLogin]: problem getting data',
      }),
    );
  }
}

function* fetchTags(action) {
  const tags = yield select(Selectors.getTags);

  const testStringCategories = action.tags;
  const regexp = /\d+/;
  const categoryArr = testStringCategories.match(regexp);

  const hasAllKeys = categoryArr.every((cat) => tags.hasOwnProperty(Number(cat)));

  if (hasAllKeys === false) {
    const response = yield call(getTags, action.tags);
    const tempObj = {};

    response.forEach((el) => {
      if (tempObj.hasOwnProperty(el.category_id) === false) {
        tempObj[el.category_id] = new Array(el);
      } else {
        tempObj[el.category_id].push(el);
      }
    });

    yield put(Actions.fetchTagsSuccess({ ...tags, ...tempObj }));
  }
}

// TODOPD-4762 - make this setAuthenticatedUser
function* setAuthenticatedUser({ authenticatedUser }) {
  try {
    yield put({ type: 'RESET' });
    yield put(Actions.setAuthenticatedUserSuccess(authenticatedUser));
    const updatedAuthenticatedUser = yield select(Selectors.selectAuthenticatedUser);

    yield put(
      OutcomesFeatureFlagsActions.fetchOutcomesFeatureFlagsSuccess(
        updatedAuthenticatedUser.tenant.configuration.outcomes_flags,
      ),
    );
    setSessionData({ authenticatedUser: updatedAuthenticatedUser });
    axiosInstance.defaults.headers.common['x-user'] = updatedAuthenticatedUser.user.uid;
  } catch (error) {
    Swal.fire({
      title: 'Error',
      text: error,
      icon: 'error',
      showCancelButton: false,
      allowOutsideClick: false,
      customClass: {
        confirmButton: 'btn btn-primary',
      },
      confirmButtonText: 'Continue',
    });
  }
}

let authenticatedUsersAbortController;
function* fetchAuthenticatedUsers() {
  yield put(Actions.setIsLoadingAuthenticatedUsers(true));

  if (authenticatedUsersAbortController) {
    authenticatedUsersAbortController.abort();
  }
  try {
    if (!authenticatedUsersAbortController) {
      authenticatedUsersAbortController = new AbortController();
    }
    const cachedAuthenticatedUser = getSessionDataByKey(AUTHENTICATED_USER);
    axiosInstance.defaults.headers.common['x-user'] = cachedAuthenticatedUser?.user.uid || DefaultUser;

    const authenticatedUsers = yield call(
      getAuthenticatedUsers,
      authenticatedUsersAbortController,
    );

    const selectedAuthenticatedUser = authenticatedUsers.find(({ current }) => current);
    axiosInstance.defaults.headers.common['x-user'] = selectedAuthenticatedUser?.user.uid;

    yield put(
      OutcomesFeatureFlagsActions.fetchOutcomesFeatureFlagsSuccess(
        selectedAuthenticatedUser.tenant.configuration.outcomes_flags,
      ),
    );
    yield put(
      Actions.fetchAuthenticatedUsersSuccess(authenticatedUsers),
    );
    yield put(Actions.setLastLogin(selectedAuthenticatedUser.user.id));
    yield put(FeatureFlagsActions.fetchFeatureFlags());

    initPendo(
      {
        email: selectedAuthenticatedUser,
        fullName: selectedAuthenticatedUser.user.full_name,
        authId: selectedAuthenticatedUser.user.auth0_user_id,
        id: `${selectedAuthenticatedUser.user.id}-${window.location.hostname}`,
        role: selectedAuthenticatedUser.role.name,
        roleId: selectedAuthenticatedUser.role.id,
        tenant: selectedAuthenticatedUser.tenant.id,
      },
      { id: selectedAuthenticatedUser.tenant.name },
    );
  } catch (error) {
    console.log(error);
    Swal.fire({
      title: 'Error',
      text: 'Error getting authenticated users',
      icon: 'error',
      showCancelButton: false,
      allowOutsideClick: false,
      customClass: {
        confirmButton: 'btn btn-primary',
      },
      confirmButtonText: 'Continue',
    });
    yield put(
      Actions.setUserErrors({
        error,
        message:
          '[fetchAuthenticatedUsers]: problem getting authenticated users',
        unauthorized: true,
      }),
    );
  } finally {
    yield put(Actions.setIsLoadingAuthenticatedUsers(false));
  }
}

export default () => ({
  [Types.FETCH_ORGANIZATIONS]: fetchOrganizations,
  [Types.SET_CHANGE_PASSWORD]: setChangePassword,
  [Types.SET_CREATE_USER]: setCreateUser,
  [Types.FETCH_TAGS]: fetchTags,
  [Types.SET_LAST_LOGIN]: setLastLogin,
  [Types.SET_AUTHENTICATED_USER]: setAuthenticatedUser,
  [Types.FETCH_AUTHENTICATED_USERS]: fetchAuthenticatedUsers,
});
